import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { userService, promoService } from '../../../services/api';
import { Loader2, Tag, X, Ticket } from 'lucide-react';
import { useLevelsData } from '../../../services/levelsService';
import ProfileCompletionModal from '../../auth/ProfileCompletionModal';
import CheckoutModal from '../../../components/payment/CheckoutModal';
import { getProvinceName } from '../../../data/canadianProvinces';

/**
 * SubscriptionModal - Modal "Prolonger" pour renouveler/upgrader un abonnement
 *
 * Affiche les options de durée et formule avec calcul dynamique du prix
 * selon les règles de gestion (extension, upgrade différentiel, etc.)
 *
 * Règles de pricing (depuis ApplicationCartDisplay.php V2) :
 * ─────────────────────────────────────────────────────────
 * CAS 0 : Pas d'abo actif → prix catalogue
 * CAS 1 : Abo via pack    → bloqué
 * CAS 2 : A examcourse, achète exam/course seul → bloqué
 * CAS 3 : Même durée ou inférieure :
 *   - < 8 jours écoulés → bloqué (trop tôt)
 *   - >= 8 jours → extension au prix catalogue, jours restants reportés
 *     (le tarif spécial 1mo←9mo — croa 2$, fiten 9$, autres 10$ — a été supprimé 07/2026)
 * CAS 4 : Upgrade (durée supérieure) :
 *   - < 8 jours → upgrade différentiel (nouveau - ancien prix)
 *   - >= 8 jours → extension au prix catalogue
 *
 * Les jours reportés sont ventilés par axe : extraDays (examens) / extraDaysCourse (cours).
 */

const DURATION_LABELS = {
    1: { fr: '1 mois', en: '1 month' },
    3: { fr: '3 mois', en: '3 months' },
    9: { fr: '9 mois', en: '9 months' },
};

const FORMULA_LABELS = {
    exam:       { fr: 'Examens', en: 'Exams' },
    course:     { fr: 'Cours', en: 'Courses' },
    examcourse: { fr: 'Examens + Cours', en: 'Exams + Courses' },
};

const FORMULA_DESCRIPTIONS = {
    exam:       { fr: 'Accès aux examens de préparation', en: 'Access to practice exams' },
    course:     { fr: 'Accès aux cours et leçons', en: 'Access to courses and lessons' },
    examcourse: { fr: 'Accès complet examens et cours', en: 'Full access to exams and courses' },
};

const SubscriptionModal = ({ levelVar, onClose }) => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const { levelsData } = useLevelsData();
    const allLevels = levelsData?.levels || [];

    const [activeLevelVar, setActiveLevelVar] = useState(levelVar);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pricingData, setPricingData] = useState(null);
    const [selectedDuration, setSelectedDuration] = useState(3);
    const [selectedFormula, setSelectedFormula] = useState('exam');
    const [showCheckout, setShowCheckout] = useState(false);

    // Profile completion & taxes
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [taxBreakdown, setTaxBreakdown] = useState(null);
    const [taxLoading, setTaxLoading] = useState(false);

    // Promo codes
    const [promoInput, setPromoInput] = useState('');
    const [appliedCodes, setAppliedCodes] = useState([]);
    const [promoError, setPromoError] = useState(null);
    const [promoLoading, setPromoLoading] = useState(false);
    const [availableCodes, setAvailableCodes] = useState([]);

    // Pack detection — find packs that include this individual level
    const originalLevelInfo = allLevels.find(l => l.var === levelVar);
    const pack = allLevels.find(l =>
        l.isPack && l.includedLevels?.includes(originalLevelInfo?.idNiveau)
    ) || null;
    // Active level info (switches between individual and pack)
    const activeLevelInfo = allLevels.find(l => l.var === activeLevelVar);
    // A pack can also be opened directly (e.g. picked in LevelsModal): activeLevelVar === levelVar
    // then, so detect it from the level itself — otherwise the individual-level payload is sent
    // with an undefined levelId ("Missing levelId or duration").
    const isPack = !!activeLevelInfo?.isPack;
    const activeLevelLabel = activeLevelInfo
        ? (language === 'fr' ? activeLevelInfo.libelleFr : activeLevelInfo.libelleEn)
        : activeLevelVar.toUpperCase();

    useEffect(() => {
        loadPricing();
    }, [activeLevelVar]);

    // Load available personal promo codes + auto-apply school code
    useEffect(() => {
        promoService.getMyCodes()
            .then(data => setAvailableCodes(data.codes || []))
            .catch(() => {});

        // Auto-apply school code from user profile
        if (user?.schoolCode) {
            setAppliedCodes(prev => {
                if (prev.some(c => c.type === 'school')) return prev;
                return [...prev, {
                    code: user.schoolCode,
                    type: 'school',
                    percentage: 10, // Will be recalculated server-side
                    label: `${user.schoolName || user.schoolCode} (${language === 'fr' ? 'École' : 'School'})`,
                    schoolName: user.schoolName || user.schoolCode,
                    autoApplied: true
                }];
            });
        }
    }, []);

    const loadPricing = async () => {
        setLoading(true);
        setError(null);
        try {
            // Pack levels: build pricing from JSON (packs don't exist in DB yet)
            const activeInfo = allLevels.find(l => l.var === activeLevelVar);
            if (activeInfo?.isPack) {
                const options = (activeInfo.pricing || []).map(p => ({
                    duration: p.duration,
                    formula: 'exam',
                    catalogPrice: p.priceExam || 0,
                    effectivePrice: p.priceExam || 0,
                    alert: '',
                    extraDays: 0,
                    isUpgrade: false,
                    message: ''
                }));
                setPricingData({
                    levelId: activeInfo.idNiveau,
                    hasCourseAvailable: false,
                    options,
                    activeSubscription: null
                });
                setSelectedFormula('exam');
            } else {
                const data = await userService.getSubscriptionPricing(activeLevelVar);
                setPricingData(data);
                // Default sur 'exam' pour TOUS les niveaux — décision UX produit. La formule
                // examen est la plus achetée ; l'user bascule manuellement vers 'course' ou
                // 'examcourse' si besoin.
                setSelectedFormula('exam');
            }
        } catch (err) {
            setError(err.message || 'Failed to load pricing');
        } finally {
            setLoading(false);
        }
    };

    // Trouver l'option correspondant à la sélection courante
    const getSelectedOption = () => {
        if (!pricingData?.options) return null;
        return pricingData.options.find(
            o => o.duration === selectedDuration && o.formula === selectedFormula
        );
    };

    const selectedOption = getSelectedOption();

    // ── Promo code logic ──
    const handleApplyPromo = async () => {
        const code = promoInput.trim();
        if (!code) return;
        if (appliedCodes.find(c => c.code.toLowerCase() === code.toLowerCase())) {
            setPromoError(language === 'fr' ? 'Code déjà appliqué' : 'Code already applied');
            return;
        }
        setPromoLoading(true);
        setPromoError(null);
        try {
            const result = await promoService.validate(code);
            if (result.valid) {
                if (result.type === 'school' && appliedCodes.some(c => c.type === 'school')) {
                    setPromoError(language === 'fr' ? 'Un seul code école par achat' : 'Only one school code per purchase');
                    return;
                }
                if (result.type === 'referral' && appliedCodes.some(c => c.type === 'referral')) {
                    setPromoError(language === 'fr' ? 'Un seul code de parrainage par achat' : 'Only one referral code per purchase');
                    return;
                }
                setAppliedCodes(prev => [...prev, {
                    code: result.code,
                    type: result.type,
                    percentage: result.percentage,
                    label: result.labels?.[language] || result.label,
                    ...(result.nomEcole && { schoolName: result.nomEcole }),
                }]);
                setPromoInput('');
            }
        } catch (err) {
            setPromoError(err.response?.data?.error || (language === 'fr' ? 'Code invalide' : 'Invalid code'));
        } finally {
            setPromoLoading(false);
        }
    };

    const handleRemoveCode = (code) => {
        setAppliedCodes(prev => prev.filter(c => c.code !== code || c.autoApplied));
    };

    const handleAddAvailableCode = (codeObj) => {
        if (appliedCodes.find(c => c.code === codeObj.code)) return;
        setAppliedCodes(prev => [...prev, {
            code: codeObj.code,
            type: 'personal',
            percentage: codeObj.percentage,
            label: `${codeObj.type === 'referral_reward' ? (language === 'fr' ? 'Récompense' : 'Reward') : (language === 'fr' ? 'Rabais' : 'Discount')} (-${codeObj.percentage}%)`
        }]);
    };

    const totalDiscount = appliedCodes.reduce((sum, c) => sum + c.percentage, 0);
    const basePrice = selectedOption?.effectivePrice || 0;
    const discountedPrice = totalDiscount > 0
        ? Math.max(0, Math.round(basePrice * (1 - totalDiscount / 100) * 100) / 100)
        : basePrice;

    // Calculate taxes when province and price are available
    useEffect(() => {
        if (!user?.province || discountedPrice <= 0) {
            setTaxBreakdown(null);
            return;
        }
        let cancelled = false;
        const calcTaxes = async () => {
            setTaxLoading(true);
            try {
                const result = await userService.calculateTaxes({
                    amount: discountedPrice,
                    province: user.province
                });
                if (!cancelled) setTaxBreakdown(result);
            } catch {
                if (!cancelled) setTaxBreakdown(null);
            } finally {
                if (!cancelled) setTaxLoading(false);
            }
        };
        calcTaxes();
        return () => { cancelled = true; };
    }, [discountedPrice, user?.province]);

    const totalToPay = taxBreakdown ? taxBreakdown.total_with_tax : discountedPrice;

    const getPaymentData = () => {
        const formulaMap = {
            exam: 'examOnly',
            course: 'noExam',
            examcourse: 'withExam',
        };

        const promoCodesPayload = appliedCodes.map(c => c.code);

        return isPack
            ? {
                packVar: activeLevelVar,
                duration: selectedDuration,
                promoCodes: promoCodesPayload,
            }
            : {
                levelId: pricingData.levelId,
                levelVar: activeLevelVar,
                duration: selectedDuration,
                formula: formulaMap[selectedFormula] || 'examOnly',
                extraDays: selectedOption.extraDays || 0,
                isUpgrade: selectedOption.isUpgrade || false,
                promoCodes: promoCodesPayload,
            };
    };

    const handlePayment = () => {
        if (!selectedOption || selectedOption.alert === 'alreadySubscribe') return;

        // Profile must be complete before payment (province needed for taxes)
        if (!user.profileCompleted) {
            setShowProfileModal(true);
            return;
        }

        setShowCheckout(true);
    };

    // ─────────────────────────────────────────────
    // Render
    // ─────────────────────────────────────────────

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 md:p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white dark:bg-gray-900 rounded-3xl max-w-lg w-full shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200 overflow-hidden max-h-[90vh] flex flex-col">

                {/* Header */}
                <div
                    className="p-6 relative overflow-hidden transition-all duration-300"
                    style={{
                        background: activeLevelInfo?.newBackgroundColor
                            ? `linear-gradient(135deg, ${activeLevelInfo.newBackgroundColor}, ${activeLevelInfo.newBackgroundColor}cc)`
                            : 'linear-gradient(135deg, #3b82f6, #6366f1)'
                    }}
                >
                    <div className="flex justify-between items-start relative z-10">
                        <div>
                            <p className="text-white/70 text-xs font-bold uppercase tracking-widest mb-1">
                                {user?.activeLevels?.[activeLevelVar]
                                    ? (language === 'fr' ? 'Prolonger l\'abonnement' : 'Extend Subscription')
                                    : user?.everPurchasedLevels?.includes(activeLevelVar)
                                        ? (language === 'fr' ? 'Redémarrer l\'abonnement' : 'Restart Subscription')
                                        : (language === 'fr' ? 'Démarrer l\'abonnement' : 'Start Subscription')}
                            </p>
                            <h3 className="text-2xl font-black text-white">
                                {activeLevelLabel}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
                        >
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                </div>

                {/* Info abonnement actif — bloc séparé entre header et body pour éviter
                    qu'il déborde du header coloré (problème de layout flex/overflow). */}
                {pricingData?.activeSubscription && (
                    <div className="px-6 pt-3 pb-0">
                        <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-4 py-2.5 text-xs">
                            <div className="flex items-center justify-between gap-3">
                                <span className="font-bold text-gray-700 dark:text-gray-200">
                                    {language === 'fr' ? 'Abo actif' : 'Active sub'}
                                    {' : '}
                                    {FORMULA_LABELS[pricingData.activeSubscription.type]?.[language] || pricingData.activeSubscription.type}
                                    {' · '}
                                    {DURATION_LABELS[pricingData.activeSubscription.duration]?.[language] || `${pricingData.activeSubscription.duration}mo`}
                                </span>
                                <span className="font-mono font-bold whitespace-nowrap text-gray-900 dark:text-white">
                                    {pricingData.activeSubscription.remainingDays}
                                    {language === 'fr' ? 'j restants' : 'd left'}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pack toggle — individual vs pack */}
                {pack && (
                    <div className="px-6 pt-4 pb-0">
                        <div className="flex bg-gray-100 dark:bg-white/5 rounded-2xl p-1 gap-1">
                            <button
                                onClick={() => setActiveLevelVar(levelVar)}
                                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all ${
                                    !isPack
                                        ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {originalLevelInfo
                                    ? (language === 'fr' ? originalLevelInfo.libelleFr : originalLevelInfo.libelleEn)
                                    : levelVar.toUpperCase()
                                }
                                {!isPack && <span className="ml-1 text-gray-400 dark:text-gray-500 font-normal">{language === 'fr' ? '(seul)' : '(only)'}</span>}
                            </button>
                            <button
                                onClick={() => setActiveLevelVar(pack.var)}
                                className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-bold transition-all ${
                                    isPack
                                        ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-900 dark:text-white'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                }`}
                            >
                                {language === 'fr' ? pack.libelleFr : pack.libelleEn}
                            </button>
                        </div>
                    </div>
                )}

                {/* Body - Scrollable */}
                <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                        </div>
                    ) : error ? (
                        <div className="text-center py-8">
                            <p className="text-red-500 mb-4">{error}</p>
                            <button onClick={loadPricing} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl text-sm font-bold">
                                {language === 'fr' ? 'Réessayer' : 'Retry'}
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Sélection formule (si cours disponibles) */}
                            {pricingData?.hasCourseAvailable && (
                                <div className="mb-6">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 block">
                                        {language === 'fr' ? 'Formule' : 'Formula'}
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['exam', 'course', 'examcourse'].map(formula => {
                                            const allBlocked = [1, 3, 9].every(dur => {
                                                const opt = pricingData?.options?.find(o => o.duration === dur && o.formula === formula);
                                                return opt?.alert === 'alreadySubscribe' || opt?.alert === 'alreadySubscribeInPack';
                                            });
                                            return (
                                            <button
                                                key={formula}
                                                onClick={() => !allBlocked && setSelectedFormula(formula)}
                                                disabled={allBlocked}
                                                className={`p-3 rounded-xl text-center transition border-2 ${
                                                    allBlocked
                                                        ? 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 cursor-not-allowed'
                                                        : selectedFormula === formula
                                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                            >
                                                <span className={`text-xs font-bold block ${
                                                    allBlocked
                                                        ? 'text-gray-500 dark:text-gray-500'
                                                        : selectedFormula === formula
                                                            ? 'text-blue-600 dark:text-blue-400'
                                                            : 'text-gray-700 dark:text-gray-300'
                                                }`}>
                                                    {FORMULA_LABELS[formula][language]}
                                                </span>
                                                {allBlocked && (
                                                    <span className="text-[9px] font-bold text-red-500 dark:text-red-400 mt-0.5 block">
                                                        {language === 'fr' ? 'Actif' : 'Active'}
                                                    </span>
                                                )}
                                            </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                                        {FORMULA_DESCRIPTIONS[selectedFormula]?.[language]}
                                    </p>
                                </div>
                            )}

                            {/* Sélection durée */}
                            <div className="mb-6">
                                <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 block">
                                    {language === 'fr' ? 'Durée' : 'Duration'}
                                </label>
                                <div className="grid grid-cols-3 gap-3">
                                    {[1, 3, 9].map(dur => {
                                        const option = pricingData?.options?.find(
                                            o => o.duration === dur && o.formula === selectedFormula
                                        );
                                        const isBlocked = option?.alert === 'alreadySubscribe' || option?.alert === 'alreadySubscribeInPack';
                                        const isBlockedByPack = option?.alert === 'alreadySubscribeInPack';
                                        const isSelected = selectedDuration === dur;

                                        return (
                                            <button
                                                key={dur}
                                                onClick={() => !isBlocked && setSelectedDuration(dur)}
                                                disabled={isBlocked}
                                                className={`relative p-4 rounded-2xl text-center transition border-2 ${
                                                    isBlocked
                                                        ? 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 cursor-not-allowed'
                                                        : isSelected
                                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 shadow-lg shadow-blue-500/10'
                                                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                }`}
                                            >
                                                <span className={`text-lg font-black block ${
                                                    isSelected
                                                        ? 'text-blue-600 dark:text-blue-400'
                                                        : 'text-gray-900 dark:text-white'
                                                }`}>
                                                    {DURATION_LABELS[dur][language]}
                                                </span>

                                                {option && !isBlocked && (() => {
                                                    const hasSpecialPrice = option.effectivePrice !== option.catalogPrice;
                                                    const optBasePrice = option.effectivePrice;
                                                    const optPromoPrice = totalDiscount > 0
                                                        ? Math.max(0, Math.round(optBasePrice * (1 - totalDiscount / 100) * 100) / 100)
                                                        : optBasePrice;
                                                    const hasPromo = totalDiscount > 0 && optPromoPrice !== optBasePrice;
                                                    // The highest original price to strike through
                                                    const strikePrice = hasSpecialPrice ? option.catalogPrice : optBasePrice;
                                                    // The final displayed price
                                                    const finalPrice = hasPromo ? optPromoPrice : optBasePrice;
                                                    const showStrike = hasSpecialPrice || hasPromo;

                                                    return (
                                                        <div className="mt-2">
                                                            {showStrike && (
                                                                <span className="text-[10px] text-gray-400 line-through block">
                                                                    {strikePrice}$
                                                                </span>
                                                            )}
                                                            <span className={`text-sm font-black ${
                                                                showStrike
                                                                    ? 'text-emerald-600 dark:text-emerald-400'
                                                                    : isSelected
                                                                        ? 'text-blue-600 dark:text-blue-400'
                                                                        : 'text-gray-500 dark:text-gray-400'
                                                            }`}>
                                                                {finalPrice}$
                                                            </span>
                                                        </div>
                                                    );
                                                })()}

                                                {isBlocked && (
                                                    <span className="text-[9px] font-bold text-red-500 dark:text-red-400 mt-1 block">
                                                        {isBlockedByPack
                                                            ? (language === 'fr' ? 'Via Pack' : 'Via Pack')
                                                            : (language === 'fr' ? 'Actif' : 'Active')
                                                        }
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── Section Code Promo ── */}
                            {selectedOption && selectedOption.alert !== 'alreadySubscribe' && selectedOption.alert !== 'alreadySubscribeInPack' && (
                                <div className="mb-6">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3 block">
                                        <Tag className="w-3 h-3 inline mr-1" />
                                        {language === 'fr' ? 'Code promo' : 'Promo code'}
                                    </label>

                                    {/* Input + button */}
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={promoInput}
                                            onChange={(e) => { setPromoInput(e.target.value); setPromoError(null); }}
                                            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleApplyPromo())}
                                            placeholder={language === 'fr' ? 'Entrez un code...' : 'Enter a code...'}
                                            className="flex-1 px-3 py-2 text-sm bg-white dark:bg-[#121212] border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition"
                                        />
                                        <button
                                            onClick={handleApplyPromo}
                                            disabled={promoLoading || !promoInput.trim()}
                                            className="px-4 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                                        >
                                            {promoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                            {language === 'fr' ? 'Appliquer' : 'Apply'}
                                        </button>
                                    </div>

                                    {/* Referral code rule (cf. stacking abuse 09-10/2026) */}
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1.5 leading-snug">
                                        {language === 'fr'
                                            ? 'Un seul code de parrainage par achat. Toute utilisation abusive des codes entraîne l\'annulation de la commande.'
                                            : 'Only one referral code per purchase. Any abusive use of codes will result in the order being cancelled.'}
                                    </p>

                                    {/* Error */}
                                    {promoError && (
                                        <p className="text-xs text-red-500 mt-1.5">{promoError}</p>
                                    )}

                                    {/* Applied codes */}
                                    {appliedCodes.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {appliedCodes.map(c => (
                                                <span
                                                    key={c.code}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/20"
                                                >
                                                    <Ticket className="w-3 h-3" />
                                                    {c.label}
                                                    {!c.autoApplied && (
                                                        <button
                                                            onClick={() => handleRemoveCode(c.code)}
                                                            className="ml-0.5 p-0.5 rounded-full hover:bg-emerald-200 dark:hover:bg-emerald-500/20 transition"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    {/* Available personal codes */}
                                    {availableCodes.length > 0 && (
                                        <div className="mt-3">
                                            <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 mb-1.5">
                                                {language === 'fr' ? 'Vos codes disponibles :' : 'Your available codes:'}
                                            </p>
                                            <div className="flex flex-wrap gap-1.5">
                                                {availableCodes
                                                    .filter(ac => !appliedCodes.find(c => c.code === ac.code))
                                                    .map(ac => (
                                                        <button
                                                            key={ac.id}
                                                            onClick={() => handleAddAvailableCode(ac)}
                                                            className="px-2.5 py-1 text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition"
                                                        >
                                                            {ac.code} (-{ac.percentage}%)
                                                        </button>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Détail de l'option sélectionnée */}
                            {selectedOption && selectedOption.alert !== 'alreadySubscribe' && selectedOption.alert !== 'alreadySubscribeInPack' && (
                                <div className="mb-6 bg-gray-50 dark:bg-white/5 rounded-2xl p-4 space-y-3">
                                    {/* Message de contexte */}
                                    {selectedOption.alert === 'extendedSubscription' && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-amber-100 dark:bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-xs font-bold text-gray-700 dark:text-gray-200">
                                                    {selectedOption.isUpgrade
                                                        ? (language === 'fr' ? 'Upgrade' : 'Upgrade')
                                                        : (language === 'fr' ? 'Extension' : 'Extension')
                                                    }
                                                </p>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                                                    {selectedOption.message}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Récapitulatif prix avec taxes */}
                                    <div className="flex flex-col gap-1.5 pt-2 border-t border-gray-200 dark:border-white/10">
                                        {/* Sous-total */}
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                                                {language === 'fr' ? 'Sous-total' : 'Subtotal'}
                                            </span>
                                            <div className="text-right">
                                                {totalDiscount > 0 && (
                                                    <span className="text-[10px] text-gray-400 line-through mr-1.5">
                                                        {selectedOption.effectivePrice}$
                                                    </span>
                                                )}
                                                <span className={`text-sm font-bold ${totalDiscount > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-700 dark:text-gray-300'}`}>
                                                    {discountedPrice}$
                                                </span>
                                                {totalDiscount > 0 && (
                                                    <span className="text-[9px] font-bold text-emerald-600 dark:text-emerald-400 ml-1">
                                                        -{totalDiscount}%
                                                        {appliedCodes.find(c => c.type === 'school')?.schoolName && (
                                                            <span className="font-normal ml-1">
                                                                — {appliedCodes.find(c => c.type === 'school').schoolName}
                                                            </span>
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Tax breakdown */}
                                        {taxBreakdown && (
                                            <>
                                                {taxBreakdown.gst > 0 && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                            TPS/GST ({taxBreakdown.rates.gst}%)
                                                        </span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {taxBreakdown.gst.toFixed(2)}$
                                                        </span>
                                                    </div>
                                                )}
                                                {taxBreakdown.qst > 0 && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                            TVQ/QST ({taxBreakdown.rates.qst}%)
                                                        </span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {taxBreakdown.qst.toFixed(2)}$
                                                        </span>
                                                    </div>
                                                )}
                                                {taxBreakdown.pst > 0 && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                            TVP/PST ({taxBreakdown.rates.pst}%)
                                                        </span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {taxBreakdown.pst.toFixed(2)}$
                                                        </span>
                                                    </div>
                                                )}
                                                {taxBreakdown.hst > 0 && (
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-[11px] text-gray-400 dark:text-gray-500">
                                                            TVH/HST ({taxBreakdown.rates.hst}%)
                                                        </span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                                            {taxBreakdown.hst.toFixed(2)}$
                                                        </span>
                                                    </div>
                                                )}
                                            </>
                                        )}

                                        {/* Note if no province yet */}
                                        {!user?.province && discountedPrice > 0 && (
                                            <p className="text-[10px] text-amber-600 dark:text-amber-400 italic">
                                                {language === 'fr'
                                                    ? 'Complétez votre profil pour voir les taxes applicables'
                                                    : 'Complete your profile to see applicable taxes'
                                                }
                                            </p>
                                        )}

                                        {taxLoading && (
                                            <div className="flex items-center gap-1.5 text-[10px] text-gray-400">
                                                <Loader2 size={10} className="animate-spin" />
                                                {language === 'fr' ? 'Calcul des taxes...' : 'Calculating taxes...'}
                                            </div>
                                        )}

                                        {/* Total TTC */}
                                        <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-white/5">
                                            <span className="text-sm font-black text-gray-700 dark:text-gray-300">
                                                Total
                                            </span>
                                            <span className="text-xl font-black text-gray-900 dark:text-white">
                                                {totalToPay.toFixed(2)}$
                                                <span className="text-xs font-normal text-gray-400 ml-1">CAD</span>
                                            </span>
                                        </div>
                                    </div>

                                    {/* Jours bonus reportés — ventilés par axe (examens / cours) */}
                                    {(selectedOption.extraDays > 0 || selectedOption.extraDaysCourse > 0) && (
                                        <div className="flex items-start gap-2 text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 rounded-lg px-3 py-2">
                                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="font-bold">
                                                {[
                                                    selectedOption.extraDays > 0
                                                        ? `+${selectedOption.extraDays} ${language === 'fr' ? 'jours Examens' : 'days Exams'}`
                                                        : null,
                                                    selectedOption.extraDaysCourse > 0
                                                        ? `+${selectedOption.extraDaysCourse} ${language === 'fr' ? 'jours Cours' : 'days Courses'}`
                                                        : null,
                                                ].filter(Boolean).join(' · ')}
                                                {language === 'fr' ? ' reportés' : ' carried over'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Message si bloqué */}
                            {(selectedOption?.alert === 'alreadySubscribe' || selectedOption?.alert === 'alreadySubscribeInPack') && (
                                <div className={`mb-6 rounded-2xl p-4 flex items-start gap-3 ${
                                    selectedOption.alert === 'alreadySubscribeInPack'
                                        ? 'bg-purple-50 dark:bg-purple-500/10'
                                        : 'bg-red-50 dark:bg-red-500/10'
                                }`}>
                                    <svg className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                                        selectedOption.alert === 'alreadySubscribeInPack'
                                            ? 'text-purple-500'
                                            : 'text-red-500'
                                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {selectedOption.alert === 'alreadySubscribeInPack' ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        )}
                                    </svg>
                                    <div>
                                        <p className={`text-sm font-bold ${
                                            selectedOption.alert === 'alreadySubscribeInPack'
                                                ? 'text-purple-600 dark:text-purple-400'
                                                : 'text-red-600 dark:text-red-400'
                                        }`}>
                                            {selectedOption.alert === 'alreadySubscribeInPack'
                                                ? (language === 'fr' ? 'Couvert par un Pack' : 'Covered by Pack')
                                                : (language === 'fr' ? 'Abonnement déjà actif' : 'Subscription already active')
                                            }
                                        </p>
                                        <p className={`text-xs mt-1 ${
                                            selectedOption.alert === 'alreadySubscribeInPack'
                                                ? 'text-purple-500/80'
                                                : 'text-red-500/80'
                                        }`}>
                                            {selectedOption.message}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer - Bouton paiement */}
                {!loading && !error && selectedOption && selectedOption.alert !== 'alreadySubscribe' && selectedOption.alert !== 'alreadySubscribeInPack' && (
                    <div className="p-6 pt-0">
                        <button
                            onClick={handlePayment}
                            disabled={discountedPrice < 0}
                            className="w-full py-3.5 rounded-2xl font-black text-white text-sm transition shadow-xl flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            {discountedPrice <= 0
                                ? (language === 'fr' ? 'Gratuit - Valider' : 'Free - Confirm')
                                : (language === 'fr'
                                    ? `Payer ${totalToPay.toFixed(2)}$ CAD`
                                    : `Pay ${totalToPay.toFixed(2)}$ CAD`
                                )
                            }
                        </button>
                        <p className="text-[10px] text-gray-400 dark:text-gray-600 text-center mt-2">
                            {language === 'fr'
                                ? `Paiement sécurisé via Stripe${user?.province ? ` · Taxes ${getProvinceName(user.province, 'fr')}` : ''}`
                                : `Secure payment via Stripe${user?.province ? ` · ${getProvinceName(user.province, 'en')} taxes` : ''}`
                            }
                        </p>
                    </div>
                )}
            </div>

            {/* Profile completion modal (shown when user tries to pay without profile) */}
            {showProfileModal && (
                <ProfileCompletionModal
                    onComplete={() => {
                        setShowProfileModal(false);
                        // Taxes will recalculate via the useEffect on user.province change
                    }}
                    onClose={() => setShowProfileModal(false)}
                />
            )}

            {/* Embedded Stripe checkout modal */}
            {showCheckout && (
                <CheckoutModal
                    paymentData={getPaymentData()}
                    onClose={() => setShowCheckout(false)}
                />
            )}
        </div>
    );
};

export default SubscriptionModal;
